import type { AiOpsInsightRunSummary } from "@closer/shared";
import {
  AI_CERTAINTY_WARNING,
  AI_OPS_INSIGHT_ADVISORY,
  AI_OPS_INSIGHT_CATEGORY_NAMES,
  AI_OPS_INSIGHT_EMPTY_MESSAGE,
  AI_OPS_INSIGHT_INVALID_REF_WARNING,
  AI_OPS_INSIGHT_PRIORITY_NAMES,
  AI_OPS_INSIGHT_UNKNOWN_ACTION_WARNING,
} from "@closer/shared";
import { Badge, Button, Card, InfoBanner, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AiAdvisoryNotice } from "../../components/AiOutputNotice";
import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDateTime } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * AUDIT / KULLANIM AI İÇGÖRÜLERİ (CLOS-291 `[195]` · CLOS-1028).
 *
 * ### ⚠ TEK BİR EYLEM DÜĞMESİ YOK
 *
 * Kart: *"Kullanıcı önerilen adımı uygulamayı seçerse ilgili kanonik akış ... çalışır."*
 * Bölümde "planı yükselt", "olayı kapat", "krediyi artır" gibi bir düğme yok; önerilen
 * adım bir CÜMLE ve kullanıcı onu abonelik/audit akışında kendi eliyle yapıyor. Düğme
 * koysaydık AI önerisi ile ticari işlem arasına tek tık kalırdı — kartın yasağı bu.
 *
 * ### ⚠ DOĞRULANAMAYAN KAYNAK GİZLENMİYOR
 *
 * Sunucu, modelin dayandığını söylediği olay türlerini incelenen dönemde gerçekten
 * görülenlerle karşılaştırıyor. Eşleşmeyen tür ekranda UYARIYLA görünüyor. Gizleseydik
 * kullanıcı uydurulmuş bir gerekçeyi doğrulanmış sanırdı; içgörüyü tümden atsaydık da
 * kısmen doğru bir örüntüyü sessizce yutmuş olurduk.
 */
export function AiOpsInsightSection() {
  const { activeTenantId, can } = useAuth();
  const { preferences } = usePreferences();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const anahtar = tenantKey(tenantId, "audit-ai-icgoruleri");
  const liste = useQuery({
    queryKey: anahtar,
    enabled: can("organization.audit"),
    queryFn: async () =>
      (await api<AiOpsInsightRunSummary[]>(`/api/audit/ai-insights`, { tenantId })).data,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();

  const uret = useMutation({
    mutationFn: async () =>
      (
        await api<AiOpsInsightRunSummary>(`/api/audit/ai-insights`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({}),
          headers: { "idempotency-key": idempotency.uret() },
        })
      ).data,
    /** ⚠ Sonuç doğrudan önbelleğe yazılıyor: tazeleme yarışına bırakılan çıktı E2E'de hiç görünmedi. */
    onSuccess: async (sonuc) => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      queryClient.setQueryData<AiOpsInsightRunSummary[]>(anahtar, (eski) => [
        sonuc,
        ...(eski ?? []),
      ]);
      await queryClient.invalidateQueries({ queryKey: anahtar });
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "subscription-overview"),
      });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "İçgörüler üretilemedi"),
  });

  /** ⚠ Yetkisiz kullanıcıda bölüm HİÇ çizilmiyor (sunucu kapısıyla aynı anahtar). */
  if (!can("organization.audit")) return null;

  const son = (liste.data ?? []).find((c) => c.status === "completed") ?? null;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">
          AI kullanım ve olay içgörüleri
        </h2>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={uret.isPending}
          disabled={uret.isPending}
          onClick={() => uret.mutate()}
        >
          {uret.isPending ? "İnceleniyor…" : son ? "Yeniden incele" : "Kayıtlarımı incele"}
        </Button>
      </div>

      <AiAdvisoryNotice />
      {/* ⚠ Kartın zorunlu cümlesi: içgörü ihlal kararı ya da fatura işlemi değil. */}
      <p className="mt-1 text-sm text-fg-muted" data-testid="ai-ops-uyari">
        {AI_OPS_INSIGHT_ADVISORY}
      </p>

      <div className="mt-4">
        {liste.isPending ? (
          <Skeleton lines={3} />
        ) : liste.error ? (
          <StateView
            state={resolveErrorState(liste.error, {
              idempotent: true,
              overrides: {
                error: { title: "İçgörüler yüklenemedi", description: "Bölüm getirilemedi." },
              },
            })}
            onRetry={() => void liste.refetch()}
          />
        ) : !son ? (
          <p className="text-sm text-fg-muted">
            Olay kayıtlarınızı ve kullanım sayaçlarınızı AI ile inceleyip eğilim çıkarabilirsiniz.
          </p>
        ) : son.insights.length === 0 ? (
          <p className="text-sm text-fg-muted" data-testid="ai-ops-bos">
            {AI_OPS_INSIGHT_EMPTY_MESSAGE}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
              <Badge tone="neutral">{son.model}</Badge>
              <span className="tabular-nums">{son.credits} kredi</span>
              <span className="tabular-nums">{son.auditCount} olay incelendi</span>
            </div>

            {son.insights.map((icgoru, i) => (
              <article
                key={i}
                data-testid={`ai-ops-icgoru-${i}`}
                className="rounded-lg border border-border-subtle p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={icgoru.priority === "high" ? "warning" : "neutral"}>
                    {AI_OPS_INSIGHT_PRIORITY_NAMES[icgoru.priority]}
                  </Badge>
                  <Badge tone="neutral">{AI_OPS_INSIGHT_CATEGORY_NAMES[icgoru.category]}</Badge>
                  <span className="text-xs text-fg-muted">{icgoru.period}</span>
                </div>

                <h3 className="mt-2 text-sm font-semibold text-fg-heading">{icgoru.title}</h3>
                <p className="mt-1 text-sm text-fg">{icgoru.rationale}</p>
                <p className="mt-2 text-sm text-fg-muted">Önerilen adım: {icgoru.suggestedStep}</p>
                {/*
                  ⚠ KESİN İFADE İŞARETİ (CLOS-1090). Cümle silinmiyor ya da düzeltilmiyor —
                  yanına ne olduğu yazılıyor. Sessizce düzeltmek AI'ın ne dediğini
                  kullanıcıdan gizlemek olurdu; reddetmek kullanıcının kredisini bir
                  kelime seçimi yüzünden yakardı. Risk ailesindeki kararla aynı.
                */}
                {icgoru.certaintyFlagged ? (
                  <p className="mt-1 text-xs text-warning" data-testid="ai-ops-kesinlik">
                    {AI_CERTAINTY_WARNING}
                  </p>
                ) : null}

                {icgoru.actions.length > 0 ? (
                  <p className="mt-2 text-xs text-fg-muted">
                    Dayandığı olay türleri: {icgoru.actions.join(", ")}
                  </p>
                ) : null}

                {/*
                  ⚠ KAYNAK ZİNCİRİ SUNUCUDAN GELİYOR (CLOS-291: içgörü kaynak olay
                  kimlikleri ve correlation zincirine bağlanır). Model kimliği hiç
                  görmüyor; buradaki değerler sunucunun çözdüğü kanonik kimlikler.
                */}
                {icgoru.sources.length > 0 ? (
                  <div className="mt-2 text-xs text-fg-muted" data-testid={`ai-ops-kaynak-${i}`}>
                    <p className="font-medium">Kaynak olaylar</p>
                    <ul className="mt-1 list-disc pl-5">
                      {icgoru.sources.map((k) => (
                        <li key={k.eventId} className="tabular-nums">
                          {k.action} · {formatDateTime(k.occurredAt, preferences.locale)}
                          {k.correlationId ? ` · zincir ${k.correlationId}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {icgoru.invalidRefs.length > 0 ? (
                  <InfoBanner tone="warning" className="mt-2">
                    <p data-testid={`ai-ops-kaynaksiz-${i}`}>
                      {AI_OPS_INSIGHT_INVALID_REF_WARNING}
                    </p>
                  </InfoBanner>
                ) : null}

                {icgoru.unknownActions.length > 0 ? (
                  <InfoBanner tone="warning" className="mt-2">
                    <div data-testid={`ai-ops-dogrulanamadi-${i}`}>
                      <p className="font-medium">{AI_OPS_INSIGHT_UNKNOWN_ACTION_WARNING}</p>
                      <p className="mt-1 text-sm">{icgoru.unknownActions.join(", ")}</p>
                    </div>
                  </InfoBanner>
                ) : null}
              </article>
            ))}

            {son.reviewNeeded.length > 0 ? (
              <InfoBanner tone="warning">
                <div data-testid="ai-ops-belirsizlik">
                  <p className="font-medium">Kesin olmayan noktalar</p>
                  <ul className="mt-1 list-disc pl-5 text-sm">
                    {son.reviewNeeded.map((satir, i) => (
                      <li key={i}>{satir}</li>
                    ))}
                  </ul>
                </div>
              </InfoBanner>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}
